import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useInputState, InputState } from 'utils/hooks/useInputState'
import { IFacility } from 'apps/fleet-planner/utils/api/facilities'
import { selectRecentFacilities } from 'apps/fleet-planner/utils/selectors'
import { getEntityById } from 'utils/getEntityById'

const ERROR_MESSAGE = 'No matching facilities found. Please select a facility or add a new one.'
const INPUT_STATES: (keyof InputState)[] = ['isDirty', 'isBlurred']

interface IGetErrorMessageArgs {
  value: Partial<IFacility> | undefined
  inputValue?: string
}

/**
 * Stores validation and input state for Facility input
 */
export const useFacilityInputState = () => {
  const facilities = useSelector(selectRecentFacilities)
  const { getErrorMessage: _getErrorMessage, onBlurTrigger, onChangeTrigger } = useInputState()

  const getErrorMessage = useCallback(
    ({ inputValue = '', value }: IGetErrorMessageArgs) => {
      const hasValueProp = !!value?.id
      const hasInputValue = !!inputValue && !!inputValue?.length
      const hasMatchingValue = !!getEntityById(facilities, value?.id)
      const inputMatchesValue = inputValue
        ?.toLowerCase()
        .includes(`${value?.name || ''}`.toLowerCase())
      const isAddOption = inputValue?.toLowerCase().includes('add ')

      /**
       * When the value in the text input doesn't match the local state value
       */
      const invalidInputStateError = _getErrorMessage(ERROR_MESSAGE, {
        inputStates: INPUT_STATES,
        conditions: [hasValueProp, !inputMatchesValue, !isAddOption],
      })

      /**
       * When there's no value at all
       */
      const invalidSelection = _getErrorMessage('Please select a facility or add a new one.', {
        inputStates: INPUT_STATES,
        conditions: [!hasValueProp, !isAddOption],
      })

      /**
       * When the user blurs the input after typing without making a selection
       */
      const hasntSelectedAValue = _getErrorMessage(ERROR_MESSAGE, {
        inputStates: INPUT_STATES,
        conditions: [!hasValueProp, hasInputValue, !isAddOption],
      })

      /**
       * When the user types a value that doesn't match anything in options
       */
      const invalidSelectionError = _getErrorMessage(ERROR_MESSAGE, {
        inputStates: INPUT_STATES,
        conditions: [hasValueProp, hasInputValue, !hasMatchingValue, !isAddOption],
      })

      return (
        invalidInputStateError || invalidSelectionError || hasntSelectedAValue || invalidSelection
      )
    },
    [_getErrorMessage, facilities]
  )

  return {
    onChangeTrigger,
    onBlurTrigger,
    getErrorMessage,
  }
}
